// Utility functions for the application

/**
 * Extract error message from API response
 */
export const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    return JSON.stringify(error.response.data.detail);
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

/**
 * Format date to French locale
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  } catch {
    return dateString;
  }
};

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Get file icon based on extension
 */
export const getFileIcon = (filename) => {
  const ext = getFileExtension(filename);
  switch (ext) {
    case 'pdf': return '📄';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif': return '🖼️';
    case 'txt': return '📝';
    default: return '📎';
  }
};

/**
 * Get file type label based on extension
 */
export const getFileTypeLabel = (filename) => {
  const ext = getFileExtension(filename);
  switch (ext) {
    case 'pdf': return 'PDF';
    case 'jpg':
    case 'jpeg': return 'Image JPEG';
    case 'png': return 'Image PNG';
    case 'gif': return 'Image GIF';
    case 'txt': return 'Texte';
    default: return 'Fichier';
  }
};

/**
 * Check if file is an image
 */
export const isImageFile = (filename) => {
  const ext = getFileExtension(filename);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

/**
 * Check if file is a PDF
 */
export const isPdfFile = (filename) => {
  return getFileExtension(filename) === 'pdf';
};

/**
 * Generate a unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
